import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { GuideMainTopics } from '@app/components/Guide/types/guide.types'
import {
  fetchDemosContent,
  fetchMainTopicSubTabsWithArticles,
  fetchTutorialsContent,
} from '@app/modules/GuideV2/service/guide.service'
import { TopicLandingItem } from '../utils/guide.utils'

export const useTopicLandingContent = (mainTopic?: string) => {
  const isTutorialsPage = mainTopic === GuideMainTopics.TUTORIALS
  const isDemosPage = mainTopic === GuideMainTopics.DEMOS

  const { data: subTabs = [], isLoading } = useQuery({
    queryKey: ['guide-main-topic-sub-tabs-with-articles', mainTopic],
    queryFn: () => fetchMainTopicSubTabsWithArticles(mainTopic!),
    enabled: Boolean(mainTopic) && !isTutorialsPage && !isDemosPage,
  })

  const { data: tutorials = [], isLoading: isTutorialsLoading } = useQuery({
    queryKey: ['tutorials-content'],
    queryFn: fetchTutorialsContent,
    enabled: isTutorialsPage,
  })

  const { data: demos = [], isLoading: isDemosLoading } = useQuery({
    queryKey: ['demos-content'],
    queryFn: fetchDemosContent,
    enabled: isDemosPage,
  })

  const items = useMemo<TopicLandingItem[]>(() => {
    if (isTutorialsPage) {
      return tutorials.map((tutorial) => ({
        id: tutorial.id,
        documentId: tutorial.documentId,
        title: tutorial.title,
        slug: tutorial.slug,
        description: tutorial.summary,
        subTabTitle: tutorial.sub_tabs?.[0]?.title,
        subTabSlug: tutorial.sub_tabs?.[0]?.slug,
        contentType: 'tutorial' as const,
      }))
    }

    if (isDemosPage) {
      return demos.map((demo) => ({
        documentId: demo.documentId,
        title: demo.title,
        slug: demo.Slug ?? (demo as { slug?: string }).slug,
        description: demo.summary,
        subTabTitle: demo.sub_tabs?.[0]?.title,
        subTabSlug: demo.sub_tabs?.[0]?.slug,
        contentType: 'demo' as const,
      }))
    }

    return subTabs.flatMap((subTab) =>
      (subTab.articles ?? []).map((article) => ({
        ...article,
        subTabTitle: subTab.title,
        subTabSlug: subTab.slug,
        contentType: 'article' as const,
      })),
    )
  }, [subTabs, tutorials, demos, isTutorialsPage, isDemosPage])

  const mainTopicContent = isTutorialsPage || isDemosPage ? undefined : subTabs[0]?.main_topic

  return {
    items,
    mainTopicContent,
    isLoading: isLoading || isTutorialsLoading || isDemosLoading,
  }
}
